import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Sparkles, Calendar, BookOpen, Users } from 'lucide-react';
import { Button } from '../ui';

const Hero = () => {
  const [isRevealed, setIsRevealed] = useState(false);

  const handleReveal = () => {
    setIsRevealed(true);
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-pragyavan-light">
      {/* Organic Background Shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-pragyavan-gold/10 blur-3xl animate-breathe" />
        <div className="absolute top-[20%] -right-[10%] w-[40%] h-[40%] rounded-full bg-sage-green/10 blur-3xl animate-breathe" style={{ animationDelay: '2s' }} />
        <div className="absolute -bottom-[10%] left-[20%] w-[60%] h-[60%] rounded-full bg-pragyavan-brown/5 blur-3xl animate-breathe" style={{ animationDelay: '4s' }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center lg:text-left"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 backdrop-blur-md border border-pragyavan-gold/30 text-pragyavan-brown text-[0.7rem] font-semibold uppercase tracking-[0.2em] mb-8 shadow-sm"
            >
              <Sparkles className="w-4 h-4 text-pragyavan-gold" />
              <span>Discover Inner Peace</span>
            </motion.div>

            {/* Main Heading */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold text-pragyavan-brown mb-6 leading-[0.95] tracking-tight text-balance">
              Awareness. <br />
              <span className="text-gradient-gold">Awakening.</span>
            </h1>

            {/* Subheading */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-xl md:text-2xl text-warm-gray mb-8 font-medium leading-relaxed text-pretty"
            >
              Meditation, movement, and mindful living in the heart of nature.
            </motion.p>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-lg text-warm-gray/80 mb-10 max-w-xl mx-auto lg:mx-0 leading-relaxed text-pretty"
            >
              Embark on a journey of self-discovery through authentic guidance, conscious breathing,
              and natural stillness in our serene sanctuary.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <Link to="/book">
                <Button
                  variant="primary"
                  size="large"
                  icon={ArrowRight}
                  iconPosition="right"
                  className="w-full sm:w-auto shadow-lg shadow-pragyavan-gold/20"
                >
                  Start Your Journey
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  variant="secondary"
                  size="large"
                  className="w-full sm:w-auto"
                >
                  Learn More
                </Button>
              </Link>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="mt-16"
            >
              <div className="grid grid-cols-3 gap-4 lg:gap-6">
                {[
                  { value: '8+', label: 'Years Serving', icon: Calendar },
                  { value: '12+', label: 'Wellness Programs', icon: BookOpen },
                  { value: '10+', label: 'Expert Instructors', icon: Users },
                ].map((stat, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ y: -4, scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                    className="relative group"
                  >
                    <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 lg:p-5 border border-white/40 shadow-sm hover:shadow-lg hover:bg-white/80 transition-all duration-300">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pragyavan-gold/20 to-pragyavan-gold/5 flex items-center justify-center group-hover:from-pragyavan-gold/30 group-hover:to-pragyavan-gold/10 transition-all duration-300">
                          <stat.icon className="w-5 h-5 text-pragyavan-gold" />
                        </div>
                        <div className="text-2xl lg:text-3xl font-bold text-pragyavan-brown">{stat.value}</div>
                      </div>
                      <div className="text-[0.65rem] lg:text-xs uppercase tracking-[0.15em] text-warm-gray/80 font-semibold pl-1">
                        {stat.label}
                      </div>
                    </div>
                    {/* Subtle glow on hover */}
                    <div className="absolute inset-0 rounded-2xl bg-pragyavan-gold/0 group-hover:bg-pragyavan-gold/5 blur-xl transition-all duration-300 -z-10" />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>

          {/* Right Column - Abstract Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="relative h-[500px] lg:h-[600px] flex items-center justify-center"
          >
            {/* Abstract Composition */}
            <div className="relative w-full h-full max-w-md mx-auto">
              {/* Background Image Layer with Zoom Animation */}
              <motion.div
                className="absolute inset-0 rounded-[2rem] overflow-hidden shadow-premium z-0"
                animate={{
                  rotate: isRevealed ? 0 : 3,
                  scale: isRevealed ? 1.3 : 1,
                  borderRadius: isRevealed ? '3rem' : '2rem'
                }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <picture>
                  <source srcSet="/hero-find-center.webp" type="image/webp" />
                  <img
                    src="/hero-find-center.png"
                    alt="Mount Kailash - Find Your Center"
                    className="w-full h-full object-cover"
                    loading="eager"
                    fetchpriority="high"
                  />
                </picture>
              </motion.div>
              {/* Subtle Gradient Overlay */}
              <AnimatePresence>
                {!isRevealed && (
                  <motion.div
                    initial={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40 rounded-[2rem] rotate-3 z-[1]"
                  />
                )}
              </AnimatePresence>

              {/* Glass Overlay with Better Contrast */}
              <AnimatePresence>
                {!isRevealed && (
                  <motion.div
                    onClick={handleReveal}
                    initial={{ opacity: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    className="absolute inset-0 bg-white/5 rounded-[2rem] -rotate-3 backdrop-blur-[2px] border border-white/20 shadow-glass z-[2] overflow-hidden cursor-pointer hover:bg-white/10 transition-colors"
                  >
                    {/* Inner Content of the visual */}
                    <div className="relative flex flex-col items-center justify-center p-8 text-center h-full z-[3]">
                      <motion.div
                        animate={{
                          scale: [1, 1.05, 1],
                          opacity: [0.9, 1, 0.9]
                        }}
                        transition={{
                          duration: 4,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                        className="w-32 h-32 rounded-full bg-gradient-to-tr from-pragyavan-gold to-accent-gold mb-8 shadow-xl flex items-center justify-center"
                      >
                        <div className="w-24 h-24 rounded-full bg-white/30" />
                      </motion.div>

                      {/* Text without Dark Background */}
                      <div className="max-w-md">
                        <h3 className="text-3xl font-display font-bold text-white mb-3 drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]">
                          Find Your Center
                        </h3>
                        <p className="text-white text-lg italic font-light leading-relaxed drop-shadow-[0_2px_6px_rgba(0,0,0,0.7)]">
                          "Quiet the mind, and the soul will speak."
                        </p>
                      </div>

                      {/* Simplified Floating Element - Just One */}
                      <motion.div
                        animate={{ y: [0, -12, 0], rotate: [0, 5, 0] }}
                        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute top-10 right-10 w-16 h-16 rounded-2xl bg-pragyavan-gold/30 backdrop-blur-sm border border-white/40 shadow-lg"
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
